import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import { ShieldAlert, ShieldCheck, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { conciliacionProveedoresApi } from '../../../lib/api';
import { fmtDate } from '../utils';

export function BloqueadosSection({ user, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unblockTarget, setUnblockTarget] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [unblockTipos, setUnblockTipos] = useState([]);

  const isGerencia = ['admin', 'gerencia'].includes((user?.rol || '').toLowerCase());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await conciliacionProveedoresApi.listBloqueados();
      setItems(res.data?.items || []);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openUnblock = (p) => {
    setUnblockTarget(p);
    setUnblockTipos(p.bloqueo_tipo || []);
  };

  const toggleTipo = (tipo) => {
    setUnblockTipos((prev) => prev.includes(tipo) ? prev.filter((t) => t !== tipo) : [...prev, tipo]);
  };

  const handleUnblock = async () => {
    if (!motivo || motivo.trim().length < 5) { toast.error('Ingresá un motivo (>=5 chars)'); return; }
    if (unblockTipos.length === 0) { toast.error('Seleccioná al menos un tipo de bloqueo a quitar'); return; }
    try {
      const res = await conciliacionProveedoresApi.desbloquearProveedor(
        unblockTarget.id, motivo, unblockTipos
      );
      toast.success(
        res.data?.sigue_bloqueado
          ? `Quitado(s): ${unblockTipos.join(', ')}. Aún bloqueado por: ${res.data.bloqueo_tipo_restante.join(', ')}`
          : 'Proveedor totalmente desbloqueado'
      );
      setUnblockTarget(null); setMotivo(''); setUnblockTipos([]);
      load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-red-600" /> Proveedores bloqueados ({items.length})</span>
          <Button size="sm" variant="ghost" onClick={load}><RefreshCw className="w-3.5 h-3.5" /></Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {loading && <Skeleton className="h-12 w-full" />}
        {!loading && items.length === 0 && <div className="text-xs text-muted-foreground py-3">Ningún proveedor bloqueado.</div>}
        {!loading && items.length > 0 && (
          <table className="w-full text-xs">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left px-2 py-1.5 border-b">Proveedor</th>
                <th className="text-left px-2 py-1.5 border-b">RUT</th>
                <th className="text-left px-2 py-1.5 border-b">Tipo</th>
                <th className="text-left px-2 py-1.5 border-b">Motivo</th>
                <th className="text-left px-2 py-1.5 border-b">Bloqueado</th>
                {isGerencia && <th className="text-right px-2 py-1.5 border-b">Acción</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b hover:bg-muted/30">
                  <td className="px-2 py-1.5 font-medium">{p.nombre}</td>
                  <td className="px-2 py-1.5 font-mono">{p.rut}</td>
                  <td className="px-2 py-1.5">
                    <div className="flex gap-1">
                      {(p.bloqueo_tipo || []).includes('pago') && (
                        <Badge variant="outline" className="bg-red-50 text-red-800 border-red-300 text-[10px]">🔴 PAGO</Badge>
                      )}
                      {(p.bloqueo_tipo || []).includes('oc') && (
                        <Badge variant="outline" className="bg-orange-50 text-orange-800 border-orange-300 text-[10px]">🟠 OC</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 text-red-700">{p.bloqueo_motivo}</td>
                  <td className="px-2 py-1.5">{fmtDate(p.bloqueo_fecha)}</td>
                  {isGerencia && (
                    <td className="px-2 py-1.5 text-right">
                      <Button size="sm" variant="outline" onClick={() => openUnblock(p)} data-testid={`btn-desbloquear-${p.id}`}>
                        <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Desbloquear
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
      <Dialog open={!!unblockTarget} onOpenChange={(v) => { if (!v) { setUnblockTarget(null); setMotivo(''); setUnblockTipos([]); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Desbloqueo manual (Gerencia)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">Proveedor: <strong>{unblockTarget?.nombre}</strong></p>
            <div>
              <label className="text-xs font-medium block mb-1.5">Tipos de bloqueo a quitar:</label>
              <div className="space-y-1.5">
                {(unblockTarget?.bloqueo_tipo || []).map((tipo) => (
                  <label key={tipo} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={unblockTipos.includes(tipo)} onChange={() => toggleTipo(tipo)} data-testid={`checkbox-unblock-${tipo}`} />
                    <span className="text-sm">
                      {tipo === 'pago' && <span className="text-red-700">🔴 Bloqueo de PAGO — permite liquidar facturas</span>}
                      {tipo === 'oc' && <span className="text-orange-700">🟠 Bloqueo de OC — permite emitir nuevas OC</span>}
                    </span>
                  </label>
                ))}
                {(!unblockTarget?.bloqueo_tipo || unblockTarget.bloqueo_tipo.length === 0) && (
                  <div className="text-xs text-muted-foreground">Sin tipos específicos registrados (desbloqueo global)</div>
                )}
              </div>
            </div>
            <Textarea placeholder="Motivo del desbloqueo (mínimo 5 caracteres)" value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} data-testid="textarea-motivo-desbloqueo" />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setUnblockTarget(null)}>Cancelar</Button>
            <Button size="sm" onClick={handleUnblock} data-testid="btn-confirmar-desbloqueo">Confirmar desbloqueo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
